import React from "react"
import { startGame, endGame, updateRoomSettings } from "./scattergoriesRoom"
import { PACKS } from "../../../data/scattergoriesLists"
import { Screen, SgButton, Pill, PlayerDot, C, F } from "./ScattergoriesUI"
import { playSound } from "../../../utils/ballparkSounds"

const LobbyScreen = ({ room, code, isHost }) => {
  const totalRounds = room.totalRounds || 3
  const duration = room.duration || 120
  const packIds = room.packIds || ["general"]

  const players = Object.entries(room.players).sort(
    (a, b) => a[1].joinedAt - b[1].joinedAt
  )

  const togglePack = async (id) => {
    if (!isHost) return
    playSound("click")
    const prev = room.packIds || ["general"]
    const next = prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    const nextPackIds = next.length ? next : prev // never allow zero packs
    await updateRoomSettings(code, { packIds: nextPackIds })
  }

  const selectAllPacks = async () => {
    if (!isHost) return
    playSound("click")
    const allIds = PACKS.map((p) => p.id)
    await updateRoomSettings(code, { packIds: allIds })
  }

  const clearAllPacks = async () => {
    if (!isHost) return
    playSound("click")
    await updateRoomSettings(code, { packIds: ["general"] })
  }

  return (
    <Screen style={{ gap: 18, maxHeight: "100dvh", overflow: "hidden", padding: "16px 20px 20px" }}>
      <div style={{ textAlign: "center", flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 2, color: C.muted, fontFamily: F.display }}>
          ROOM CODE
        </div>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 48, letterSpacing: 2, color: C.indigo }}>
          {code}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.muted }}>
          Friends join at <span style={{ color: C.indigo, fontWeight: 800 }}>ryandymock.com/scattergories</span>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 18, paddingRight: 4, margin: "4px 0" }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.5, color: C.muted, fontFamily: F.display }}>
            PLAYERS · {players.length}
          </div>
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            {players.map(([pid, p]) => (
              <div
                key={pid}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: C.card,
                  border: `1.5px solid ${C.border}`,
                  borderRadius: 12,
                  padding: "10px 14px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                }}
              >
                <PlayerDot color={p.color} />
                <span style={{ fontWeight: 800, fontSize: 15, color: C.ink }}>{p.name}</span>
                {pid === room.hostId && <Pill style={{ marginLeft: "auto" }}>HOST</Pill>}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.5, color: C.muted, fontFamily: F.display }}>
            ROUNDS
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            {[3, 5, 8].map((n) => (
              <SgButton
                key={n}
                variant={totalRounds === n ? "indigo" : "panel"}
                onClick={() => {
                  if (isHost) {
                    playSound("click")
                    updateRoomSettings(code, { totalRounds: n })
                  }
                }}
                disabled={!isHost}
                style={{
                  height: 44,
                  flex: 1,
                  cursor: isHost ? "pointer" : "default",
                  opacity: !isHost ? (totalRounds === n ? 1 : 0.45) : 1,
                }}
              >
                {n}
              </SgButton>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.5, color: C.muted, fontFamily: F.display }}>
            ROUND TIMER
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            {[60, 90, 120, 150, 180].map((sec) => (
              <SgButton
                key={sec}
                variant={duration === sec ? "indigo" : "panel"}
                onClick={() => {
                  if (isHost) {
                    playSound("click")
                    updateRoomSettings(code, { duration: sec })
                  }
                }}
                disabled={!isHost}
                style={{
                  height: 44,
                  flex: "1 0 50px",
                  cursor: isHost ? "pointer" : "default",
                  opacity: !isHost ? (duration === sec ? 1 : 0.45) : 1,
                  fontSize: 14,
                }}
              >
                {sec}s
              </SgButton>
            ))}
          </div>
        </div>

        <div>
          <div style={{ display: "flex", alignItems: "center", justifycontent: "space-between" }}>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.5, color: C.muted, fontFamily: F.display }}>
              CATEGORY PACKS
            </div>
            {isHost && (
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={selectAllPacks}
                  style={{
                    background: "none",
                    border: "none",
                    color: C.indigo,
                    fontFamily: F.display,
                    fontWeight: 800,
                    fontSize: 12,
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  Select All
                </button>
                <span style={{ color: C.borderDark, fontSize: 12 }}>|</span>
                <button
                  onClick={clearAllPacks}
                  style={{
                    background: "none",
                    border: "none",
                    color: C.indigo,
                    fontFamily: F.display,
                    fontWeight: 800,
                    fontSize: 12,
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  Clear All
                </button>
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8, maxHeight: 180, overflowY: "auto", paddingRight: 4 }}>
            {PACKS.map((pack) => {
              const on = packIds.includes(pack.id)
              return (
                <div
                  key={pack.id}
                  onClick={() => isHost && togglePack(pack.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: on ? C.highlight : C.card,
                    border: on ? `2px solid ${C.indigo}` : `1.5px solid ${C.border}`,
                    borderRadius: 12,
                    padding: "8px 12px",
                    cursor: isHost ? "pointer" : "default",
                    opacity: !isHost && !on ? 0.6 : 1,
                    transition: "all 0.2s ease",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 14, color: C.ink }}>{pack.name}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted }}>
                      {pack.blurb}
                    </div>
                  </div>
                  <span style={{ marginLeft: "auto", fontWeight: 900, color: on ? C.indigo : C.borderDark, fontSize: 16 }}>
                    {on ? "✓" : "○"}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10, flexShrink: 0 }}>
        {isHost ? (
          <>
            <SgButton
              onClick={() => {
                playSound("click")
                startGame(code, { totalRounds, duration, packIds })
              }}
              disabled={players.length < 2}
            >
              {players.length < 2 ? "Waiting for players…" : "Start Game"}
            </SgButton>
            <SgButton variant="ghost" onClick={() => endGame(code)}>
              Cancel Game
            </SgButton>
          </>
        ) : (
          <div style={{ textAlign: "center", fontWeight: 700, color: C.muted, fontFamily: F.display }}>
            Waiting for the host to start…
          </div>
        )}
      </div>
    </Screen>
  )
}

export default LobbyScreen
